/**
 * ContractsSection - Gestion des Mandats de Recherche sur la fiche lead.
 * Permet de creer, envoyer, annuler et telecharger le mandat signe.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const map = {
    sent: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En attente de signature' },
    signed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Signe' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Annule' },
    draft: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Brouillon' },
  };
  const m = map[status] || map.draft;
  return <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${m.bg} ${m.text}`}>{m.label}</span>;
};

const ContractsSection = ({ leadId, leadName }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [linkToCopy, setLinkToCopy] = useState(null);

  const fetchContracts = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/contracts?lead_id=${leadId}`)
      .then(r => setContracts(r.data || []))
      .catch(() => setContracts([]))
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = {
        lead_id: leadId,
        duree_mois: 12,
        notes: notes || null,
      };
      const r = await axios.post(`${API}/contracts`, payload);
      setLinkToCopy(r.data.sign_link);
      setShowModal(false);
      setNotes('');
      fetchContracts();
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreating(false);
    }
  };

  const handleResend = async (contractId) => {
    try {
      const r = await axios.post(`${API}/contracts/${contractId}/resend`);
      if (r.data.email_sent) alert('Email renvoye avec succes.');
      else alert('Email non envoye (verifiez Resend ou email client).');
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    }
  };

  const handlePushToDrive = async (contract) => {
    if (!window.confirm(`Pousser le mandat signe ${contract.number} dans le dossier Drive admin du client ?`)) return;
    try {
      const r = await axios.post(`${API}/contracts/${contract.contract_id}/push-to-drive`);
      alert('Mandat archive dans le Drive client (Doc admins clients).');
      if (r.data.url) window.open(r.data.url, '_blank');
      fetchContracts();
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleResendDocsRequest = async (contractId) => {
    if (!window.confirm("Renvoyer l'email de demande de pieces (CNI, bulletins, avis impots, releves, epargne) au client ?")) return;
    try {
      const r = await axios.post(`${API}/contracts/${contractId}/resend-documents-request`);
      alert(`Email envoye a ${r.data.to}.`);
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleCancel = async (contractId) => {
    if (!window.confirm('Annuler ce mandat ? Le lien de signature sera desactive.')) return;
    try {
      await axios.post(`${API}/contracts/${contractId}/cancel`);
      fetchContracts();
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDownload = async (contract) => {
    try {
      const r = await axios.get(`${API}/contracts/${contract.contract_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const suffix = contract.status === 'signed' ? '_signe' : '';
      a.download = `Mandat_${contract.number}${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    alert('Lien copie dans le presse-papier');
  };

  return (
    <div className="realease-card p-6" data-testid="contracts-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-gray-900">Mandat de Recherche</h3>
          <p className="text-xs text-gray-500">Signature electronique</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-3 py-1.5 bg-[#008A8D] text-white rounded-lg text-sm font-semibold hover:bg-[#006B6D] flex items-center gap-1.5"
          data-testid="create-contract-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Envoyer un mandat
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Chargement...</div>
      ) : contracts.length === 0 ? (
        <div className="text-sm text-gray-400 italic py-3">Aucun mandat envoye pour ce client.</div>
      ) : (
        <div className="space-y-2">
          {contracts.map(c => (
            <div key={c.contract_id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50" data-testid={`contract-row-${c.contract_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{c.number}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Envoye le {new Date(c.created_at).toLocaleDateString('fr-FR')}
                    {c.signed_at && <> | Signe le {new Date(c.signed_at).toLocaleDateString('fr-FR')}</>}
                  </p>
                  {c.status === 'signed' && c.signed_full_name && (
                    <p className="text-[11px] text-green-700 mt-0.5">Par <b>{c.signed_full_name}</b></p>
                  )}
                  {c.status === 'signed' && c.drive_file_url && (
                    <a href={c.drive_file_url} target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-[#008A8D] hover:underline mt-0.5 inline-flex items-center gap-1"
                       data-testid={`drive-link-${c.contract_id}`}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                      Archive Drive
                    </a>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => handleDownload(c)} className="p-1.5 hover:bg-gray-100 rounded" title="Telecharger le PDF" data-testid={`download-contract-${c.contract_id}`}>
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" /></svg>
                  </button>
                  {c.status === 'signed' && (
                    <>
                      {!c.drive_file_id && (
                        <button onClick={() => handlePushToDrive(c)} className="p-1.5 hover:bg-teal-50 rounded text-[#008A8D]" title="Archiver dans le dossier Drive client" data-testid={`push-drive-${c.contract_id}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                        </button>
                      )}
                      <button onClick={() => handleResendDocsRequest(c.contract_id)} className="p-1.5 hover:bg-amber-50 rounded text-amber-700" title="Renvoyer l'email demande de pieces" data-testid={`resend-docs-${c.contract_id}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      </button>
                    </>
                  )}
                  {c.status === 'sent' && (
                    <>
                      <button onClick={() => handleResend(c.contract_id)} className="p-1.5 hover:bg-gray-100 rounded" title="Renvoyer l'email">
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      </button>
                      <button onClick={() => handleCancel(c.contract_id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Annuler">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal creation */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="create-contract-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Envoyer le Mandat de Recherche</h3>
            <p className="text-xs text-gray-500 mb-4">Pour <b>{leadName}</b>. Le client recevra un email avec lien de signature electronique.</p>
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-xs text-teal-900 mb-4">
              Le contrat officiel RealEase (12 articles + bareme degressif + 3 livrets annexes) sera joint automatiquement.
              Les honoraires seront calcules a la signature de l'acte selon le bareme du contrat.
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700">Notes internes (optionnel, non visible par le client)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg mt-1 h-20 resize-none" placeholder="Ex: Client prioritaire, preferences, contexte..." />
            </div>

            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => { setShowModal(false); setNotes(''); }} className="px-4 py-2 border border-gray-300 rounded-lg">Annuler</button>
              <button onClick={handleCreate} disabled={creating}
                      className="px-4 py-2 bg-[#008A8D] text-white rounded-lg font-semibold hover:bg-[#006B6D] disabled:opacity-50"
                      data-testid="submit-create-contract">
                {creating ? 'Envoi...' : 'Creer et envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal link copy */}
      {linkToCopy && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Mandat cree et email envoye</h3>
            <p className="text-sm text-gray-600 mb-4">Le client a recu un email avec le lien de signature. Vous pouvez egalement lui transmettre ce lien par un autre canal :</p>
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs font-mono break-all">{linkToCopy}</div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => copyLink(linkToCopy)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">Copier le lien</button>
              <button onClick={() => setLinkToCopy(null)} className="px-3 py-1.5 bg-[#008A8D] text-white rounded-lg text-sm font-semibold">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractsSection;
